import React from "react";
import {BaseComponent} from "@renta-apps/athenaeum-react-common";
import {getLabelColSpan, ITotalsLayout, renderTotalsValueCells} from "../TotalsTable/TotalsLayout";
import UserSalaryHoursTotals from "../../../../models/server/UserSalaryHoursTotals";
import Localizer from "../../../../localization/Localizer";

import styles from "../TotalsTable/TotalsTable.module.scss";

interface IAllPagesTotalsProps {
    layout: ITotalsLayout;

    /**
     * Fetches the totals of the whole filtered result set with the current filters. Must reject (not navigate away) on failure.
     */
    fetchTotals(): Promise<UserSalaryHoursTotals>;
}

interface IAllPagesTotalsState {
    loading: boolean;
    error: boolean;
    totals: UserSalaryHoursTotals | null;
}

/**
 * "Total (all pages)" row of the Daily hours totals table.
 * Loads the server-side totals when mounted (the report is opened or the Daily hours type is selected) and again only when the owner calls {@link reloadAsync}
 * (filters changed, or hours were saved, deleted, restored or added). Paging, page size and sorting do not reload it.
 */
export default class AllPagesTotals extends BaseComponent<IAllPagesTotalsProps, IAllPagesTotalsState> {

    state: IAllPagesTotalsState = {
        loading: true,
        error: false,
        totals: null,
    };

    /**
     * Incremented for every request, so a slow response of an older request never overwrites the result of a newer one.
     */
    private _requestId: number = 0;

    public async initializeAsync(): Promise<void> {
        await super.initializeAsync();
        await this.reloadAsync();
    }

    public async reloadAsync(): Promise<void> {
        const requestId: number = ++this._requestId;

        await this.setState({loading: true, error: false});

        let totals: UserSalaryHoursTotals | null = null;
        let error: boolean = false;

        try {
            totals = await this.props.fetchTotals();
        } catch (e) {
            error = true;
        }

        if ((requestId === this._requestId) && (this.isMounted)) {
            await this.setState({loading: false, error, totals: (error) ? null : totals});
        }
    }

    /**
     * While loading or on error the status text is shown next to the label, in one cell spanning the rest of the row.
     */
    private renderStatus(labelColSpan: number): React.ReactNode {
        const {loading} = this.state;

        return (
            <td className={(loading) ? styles.status : styles.error}
                colSpan={Math.max(this.props.layout.accessors.length - labelColSpan, 1)}
            >
                {(loading) ? Localizer.genericLoading : Localizer.workDayPanelAllPagesTotalError}
            </td>
        );
    }

    public render(): React.ReactNode {
        const {loading, error, totals} = this.state;
        const layout: ITotalsLayout = this.props.layout;
        const labelColSpan: number = getLabelColSpan(layout);
        const loaded: boolean = (!loading) && (!error) && (totals != null);

        return (
            <tr id="workDayAllPagesTotals"
                className={styles.allPagesTotalsRow}
                data-state={(loading) ? "loading" : (error) ? "error" : "loaded"}
            >

                <td id="workDayAllPagesTotalsLabel"
                    className={styles.label}
                    colSpan={labelColSpan}
                >
                    {Localizer.workDayPanelAllPagesTotal}
                </td>

                {
                    (loaded)
                        ? renderTotalsValueCells(layout, totals!, "workDayAllPagesTotal_", styles.value)
                        : this.renderStatus(labelColSpan)
                }

            </tr>
        );
    }
}
